import { network } from '@tencent/netcheck';
import i18n from '@i18next/i18n';
import {
    IEditorUpdateObj,
    ILinkEditMenuService,
    ILinkFloatBarService,
    ILinkManager,
    ILinkManagerInjectable,
} from './link-manager.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { GraphType } from '@src/slide/infrastructure/module/dependent/shape-info/constant';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { isCorrectEmail, isStrictlyCorrectUrl } from '@src/utils/verify-link';
import { transMailObj2Str, transMailStr2Obj } from '@src/slide/domain/ui-model/element-modeles/property/link/ui-sdk-transform';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { EMAIL_PREFIX } from '@src/slide/domain/ui-model/element-modeles/property/link/constant/prefix';
import { getRedirectUrl } from '@src/js/common/utils';
import { processSavedStyles } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/text-util';
import axios from 'axios';
import { NOTE_CANVAS_ID } from '@com-pc/note-area-wrapper/constant';
import { AutoFitType, WrapType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { AddonMode } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import { DEFAULT_MIMETYPE } from '@src/js/constant';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { isMultiplyModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IAddonManager } from '@src/slide/application/service/addon/addon-manager.interface';
import { IPlayManager } from '@src/slide/application/service/play-service/play-manager.interface';
import EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { ICanvasSize } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/type';
import { IEditorInstanceService } from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { jumpToPage } from '@src/framework/player/src/actions/control';
import { SameLinkTextElementsHelper } from '@src/slide/application/service/link-manager/core/same-link-text-element-helper';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { Editor, TextElement } from '@tencent/alloy-editor';
import { LinkUtils } from '@src/slide/application/service/link-manager/core/link-utils';
import { InteractionEvent } from '@tencent/alloy-renderer';
import { Store } from 'redux';
import { LinkEditMenuService } from '@src/slide/application/service/link-manager/core/link-edit-menu/link-edit-menu-service';
import { LinkFloatBarService } from '@src/slide/application/service/link-manager/core/link-floatbar/link-float-bar-service';
import { LINK_IS_INVALID_FLAG } from '@src/slide/domain/ui-model/element-modeles/property/link/constant';
import get from 'lodash/get';
import { IElementModelProps } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/element-model-props.interface';
import { getSlideTarget } from '@src/slide/domain/ui-model/element-modeles/property/link/utils/jump-to-slide-by-id';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { getXSRF } from '@src/slide/domain/slide-app/utils/cookie-utils';

const CONTAINER_ID = 'canvas';
const hostname = window.hostname || window.location.hostname;

/**
 * 所有链接相关逻辑整理
 */
export default class LinkManager implements ILinkManager {
    public getCanvasSize?: () => ICanvasSize;
    public playerInFrame = false;
    public readonly linkEditMenuService: ILinkEditMenuService;
    public readonly linkFloatBarService: ILinkFloatBarService;
    public linkUtils: LinkUtils;
    private editMenuTimer: ReturnType<typeof setTimeout> | null;

    public constructor(
        private uiDataService: IUiDataService,
        private selectionService: ISelectionService,
        private addonManager: IAddonManager,
        private playManager: IPlayManager,
        private editingStateManager: EditingStateManager,
        private editorInstanceService: IEditorInstanceService,
        private userPriviledgeService: IUserPriviledgeService,
        private injectables?: ILinkManagerInjectable,
        private menuManager?: IMenuManager,
    ) {
        this.editMenuTimer = null; // 存在时说明editMenu在待开启状态，此时屏蔽链接工具栏
        this.linkUtils = new LinkUtils(uiDataService, selectionService);
        this.linkEditMenuService = new LinkEditMenuService(uiDataService, this, menuManager);
        this.linkFloatBarService = new LinkFloatBarService(addonManager);
    }

    public setMenuManagerDelegate(menuManager: IMenuManager) {
        if (this.menuManager) return;
        this.menuManager = menuManager;
        this.linkEditMenuService.menuManager = menuManager;
    }

    public async requestLinkData(link: any) {
        if (!(this.injectables?.supportLinkInfo)) {
            return Promise.resolve(null);
        }
        const url = `https://${hostname}/v2/file/link/getinfo?xsrf=${getXSRF()}`;
        return axios({
            method: 'post',
            url,
            timeout: 2000,
            data: JSON.stringify({
                docUrl: link,
            }),
            headers: { 'Content-type': 'application/json' },
        }).then(response => response.data);
    }

    /**
     * 插入文本框，设置链接
     */
    public insertTextWithLink() {
        const props = {
            style: {
                position: 'absolute',
                left: (this.uiDataService.presentation.props.slideWidth / 2) - 200,
                top: this.uiDataService.presentation.props.slideHeight / 4,
                bgShape: GraphType.RECT,
                border: { width: 1, style: 'solid' },
            },
            subType: 1,
        };
        const extraInfo = {
            textBodyStyle: {
                autoFitType: AutoFitType.SHAPE_AUTO_FIT,
                bodyStyle: { whiteSpace: WrapType.NO_WRAP },
            },
        };

        const { currentSlideIndex } = this.selectionService;
        const slideModel = this.selectionService.slides[currentSlideIndex];
        slideModel.addElement([UIModelType.SHAPE], [props], [extraInfo]);

        setTimeout(() => {
            const model: any = this.selectionService.getSelectedModel();
            if (model.editorInstance) {
                model.editorInstance.setFocus(() => {
                    model.editorInstance?.setTextLink();
                });
            }
        }, 200);
    }

    public getLinkColor = (textElement: TextElement, slideNum?: number) => {
        if (textElement['origin-href']?.colorType === 'tx') {
            return textElement.color;
        }
        return this.getLinkThemeColor(slideNum);
    };

    /**
     * 判断当前选中区是否有链接样式
     * @param editor
     * @returns {boolean}
     */
    public getTextLinkState(editor: Editor): boolean {
        return !!this.getCurrentLinkInfo(editor);
    }

    /**
     * 回车生成链接，在before和after之间执行
     * @param editor
     */
    public setLinkWhenEnter = (editor: Editor, updateDataCB) => {
        if (!editor.selection || this.isSelectionHasLink(editor)) {
            return;
        }

        const caretPoint = editor.getStartPoint()!;
        const [paragraph, paragraphPath] = editor.getParent(caretPoint.path);

        const paragraphStart = editor.getStartPoint(paragraphPath);
        const text = editor.getString({
            anchor: paragraphStart,
            focus: caretPoint,
        } as any);

        if (text) {
            const hasRightSpace = text.match(/(\s+$)/g);
            if (hasRightSpace) {
                return;
            }
            const linkText = text.replace(/^\s+|\s+$/g, ''); // 去掉两端的空格
            const linkData = this.verifyTextToData(linkText);
            if (linkData) {
                // 这个段落里如果存在链接，则不执行文本转链接
                if (this.isParagraphHasLink(paragraph)) return;

                const linkStart = editor.getBeforePoint(caretPoint, text.length);
                const linkRange: any = {
                    anchor: linkStart,
                    focus: caretPoint,
                };
                editor.changeTextProps({
                    href: linkData,
                    'origin-href': linkData,
                    'origin-raw-href': true,
                }, linkRange);
                if (text.toLowerCase().includes(hostname)) {
                    this.renameLinkData(editor, text, linkRange, linkData, updateDataCB);
                } else {
                    // 生成链接后保存
                    updateDataCB?.();
                }
            }
        }
    };

    /**
     * 给移动端使用
     * @param e
     */
    public showLinkFloatBarMobile = (e: InteractionEvent, editor?: Editor) => {
        const textElement = (e.currentTarget as any)?.textElement || {};
        const data = textElement['origin-href'];
        const href = this.getLinkUrlWithData(data);
        const rectNode = e.currentTarget;
        if (rectNode) {
            setTimeout(() => {
                const isInNoteArea = this.isInNoteArea();
                const sameLinkTextElements = SameLinkTextElementsHelper.getSameLinkTextElements(textElement, editor);
                const anchorRect = this.processFloatBarAnchorRect(sameLinkTextElements, isInNoteArea);
                const hasChinese = data.slideJump;
                this.linkFloatBarService.show({
                    left: anchorRect.left,
                    top: anchorRect.top,
                    href,
                    editable: false,
                    anchorView: browser.isMobile ? textElement : null,
                    hasChinese,
                    openLink: () => {
                        this.openLink(data);
                        this.linkFloatBarService.hide();
                        // 安卓 qq 小程序中跳转页面之后不会自动收起键盘，所以需要手动处理下
                        // 如果当前有正在编辑的元素，手动失焦触发收起键盘
                        if (this.editorInstanceService.checkEditorIsEditing()) {
                            (document.activeElement as HTMLElement | undefined)?.blur?.();
                        }
                    },
                    importConfig: this.linkFloatBarService.getDisplayPrivilege(null), // 不是iframeLink
                    processFloatBarAnchorRect: () => {
                        this.processFloatBarAnchorRect(sameLinkTextElements, isInNoteArea);
                    },
                    addonManager: this.addonManager,
                    menuManager: this.menuManager,
                    selectionService: this.selectionService,
                });
            }, 100);
        }
    };

    /**
     * 处理链接跳转
     * @param store 这个store是演示态的redux，命名最好区分下
     * @param data
     */
    public openLinkInPlay = (store?: Store, data?: any) => {
        if (data && store) {
            if (data.slideJump) {
                const state = store.getState();
                const control = state?.control;
                if (control) {
                    const curPage = control.currentPage;
                    const { pageCount } = control;
                    const page = this.getJumpPage(data.slideJump, curPage, pageCount);
                    store.dispatch(jumpToPage(page));
                }
            } else {
                let href = this.getLinkUrlWithData(data);
                if (this.playerInFrame === true) {
                    href = getRedirectUrl(href);
                    eventBus.emit(EventKeys.LINK_CLICKED_IN_FRAME, href);
                } else {
                    this.linkFloatBarService.openUrl(href);
                    eventBus.emit(EventKeys.LINK_CLICKED);
                }
            }
        }
    };

    // eslint-disable-next-line complexity
    public handleLinkMouseEnter = (e: InteractionEvent, editorObj: IEditorUpdateObj, option) => {
        const { textElement } = e.currentTarget as any;
        const hrefInfo = textElement['origin-href'];
        this.updateTitleShowStatus(e.currentTarget?.worldBounds, hrefInfo, true);

        if (!option.selected) return;

        if (!editorObj?.editor || this.editMenuTimer || this.linkEditMenuService.isShown()) return;

        // 播放中（PlayManager.isPlaying）不展示浮层
        if (this.playManager.isPlaying) return;

        if (this.editingStateManager.isInAnyEditingState()) return;

        this.linkFloatBarService.clearLeaveTimer();
        this.showLinkFloatBarPC(e, editorObj, option);
    };

    public handleLinkMouseLeave = (e, option) => {
        const hrefInfo = e.currentTarget?.textElement?.['origin-href'];
        this.updateTitleShowStatus(e.currentTarget?.worldBounds, hrefInfo, false);
        if (!option.selected) {
            return;
        }

        this.linkFloatBarService.clearLeaveTimer();
        if (this.linkFloatBarService.isShown()) {
            this.linkFloatBarService.hideWithTimeout(500);
        }
    };

    /**
     * 获取链接信息并打开链接面板
     * @param {*} editorObj
     */
    public getLinkDataAndSetLink = (editorObj: IEditorUpdateObj) => {
        const linkData = this.getCurrentLinkData(editorObj.editor);
        // 获取链接过程中可能会选中文字，加异步是为了在选区位置更新之后再打开链接面板，避免位置计算错误
        setTimeout(() => {
            this.setTextLink(editorObj, linkData);
        }, 0);
    };

    public getLinkUrlWithData(linkData) {
        return linkData.url || (linkData.mail ? transMailObj2Str(linkData.mail) : '') || (linkData.slideJump ? this.getInnerLinkInfo(linkData.slideJump, { prefix: i18n.t('链接至') }) : '');
    }

    /**
     * 获取跳转的幻灯片索引页
     * @param slideJump
     * @param curIndex
     * @param count
     */
    public getJumpPage(slideJump, curIndex, count) {
        let page = curIndex;

        if (slideJump?.action) {
            const index = slideJump.action.indexOf('?');
            if (index > 0) {
                page = this.getPageWithQuestionMarkInAction(slideJump, index, curIndex, count);
            } else if (slideJump.target) {
                const target = getSlideTarget(slideJump.target, this.uiDataService.presentation.children);
                page = parseInt(target, 10);
                if (isNaN(page)) {
                    page = curIndex;
                } else {
                    page = page - 1;
                }
            }
        }

        if (page < 0) page = 0;
        if (page >= count) page = count - 1;

        return page;
    }

    // eslint-disable-next-line complexity
    public getInnerLinkInfo(slideJump, config: any = {}) {
        if (!slideJump) return;
        let info = i18n.t('下一张幻灯片');
        if (slideJump.action) {
            const index = slideJump.action.indexOf('?');
            if (index > 0) {
                const subStr = slideJump.action.substring(index + 6);
                switch (subStr) {
                    case 'previousslide':
                        info = i18n.t('上一张幻灯片');
                        break;
                    case 'firstslide':
                        info = i18n.t('第一张幻灯片');
                        break;
                    case 'lastslide':
                        info = i18n.t('最后一张幻灯片');
                        break;
                    case 'nextslide':
                        info = i18n.t('下一张幻灯片');
                }
            } else if (slideJump.target) {
                const target = getSlideTarget(slideJump.target, this.uiDataService.presentation.children);

                if (target.indexOf(LINK_IS_INVALID_FLAG) !== -1) {
                    return i18n.t('链接已失效, 请重新设置');
                }

                info = `${i18n.t('幻灯片')} ${target}`;
            }
        }

        if (config.prefix) {
            return `${config.prefix}${info}`;
        }

        return info;
    }

    public getLinkInfo = (props: IElementModelProps) => {
        const href = get(props, 'link.href');
        if (!href) {
            return {};
        }

        return {
            tooltip: this.getToolTip(href, true),
            href,
        };
    };

    public getToolTip(link, isPlayState) {
        if (!isPlayState) {
            return;
        }

        let href = this.getLinkUrlWithData(link);
        if (href?.indexOf(EMAIL_PREFIX) === 0) {
            href = href.substring(EMAIL_PREFIX.length);
        }

        try {
            return link.tooltip || decodeURI(href);
        } catch (e) {
            return href;
        }
    }

    private getPageWithQuestionMarkInAction(slideJump, index, curIndex, count) {
        let page = curIndex;
        const subStr = slideJump.action.substring(index + 6);
        switch (subStr) {
            case 'previousslide':
                page = page - 1;
                break;
            case 'firstslide':
                page = 0;
                break;
            case 'lastslide':
                page = count - 1;
                break;
            case 'nextslide':
                page = page + 1;
        }
        return page;
    }

    private getCanvasRect = (isInNote) => {
        let canvasElem: HTMLElement | null = null;
        if (isInNote) {
            canvasElem = document.getElementById(NOTE_CANVAS_ID);
        } else {
            canvasElem = document.getElementById(CONTAINER_ID);
        }
        const canvasRect = canvasElem?.getBoundingClientRect();
        // 定位时减去可能存在的滚动
        return {
            left: (canvasRect?.left ?? 0) - (canvasElem?.scrollLeft || 0),
            top: (canvasRect?.top ?? 0) - (canvasElem?.scrollTop || 0),
        };
    };

    private processAnchorRect = (selRect, isInNote) => {
        if (!selRect) {
            return {
                x: 0, y: 0, width: 0, height: 0,
            };
        }
        const canvasRect = this.getCanvasRect(isInNote);
        return {
            x: selRect.left + (selRect.width / 2) + canvasRect.left,
            y: selRect.top + canvasRect.top - 4,
            width: 0,
            height: (selRect.height || 0) + 8,
        };
    };

    private processFloatBarAnchorRect = (textElements, isInNote) => {
        const canvasRect = this.getCanvasRect(isInNote);
        const filterTextElements = textElements.filter(text => !!text?.text);
        const bounds = SameLinkTextElementsHelper.getBounds(filterTextElements);
        let translateX = 0;
        let translateY = 0;
        if (browser.isMobile) {
            const canvasSize = this.getCanvasSize?.();
            translateX = canvasSize?.left ?? 0;
            translateY = canvasSize?.top ?? 0;
        }
        return {
            left: bounds.minX + canvasRect.left + translateX,
            top: bounds.maxY + canvasRect.top + translateY,
            width: bounds.maxX - bounds.minX,
            height: bounds.maxY - bounds.minY,
        };
    };

    /**
     * 设置链接
     * @param editor 所属Editor
     */
    private setTextLink = (editorObj: IEditorUpdateObj, linkData) => {
        if (!editorObj?.editor) return;

        const anchorRect = this.processAnchorRect(editorObj.editor.getSelectionRect(), this.isInNoteArea());
        let data: any = {};
        let hasHrefInFirstMarks = false;
        const currentLinkData = this.getCurrentLinkData(editorObj.editor, linkData);
        if (currentLinkData) {
            hasHrefInFirstMarks = true;
            data = currentLinkData;
        }

        this.linkEditMenuService.show({
            anchorRect,
            data,
            handleLinkRemove: () => {
                if (editorObj.editor) {
                    this.removeTextLink(editorObj.editor, editorObj.updateDataCB);
                }
                this.linkEditMenuService.hide();
            },
            handleLinkEdit: (value) => {
                // 如果当前已经丢失了 editor 或者失焦，则直接关闭编辑面板
                if (editorObj.editor?.selection) {
                    if (!data.mimeType || !hasHrefInFirstMarks || !value.url) {
                        const linkProps = this.processSavedStylesWithColorType(value, data.colorType, editorObj.editor);
                        if (value.text === editorObj.editor?.getString()) {
                            // 没有更改文字，只修改了链接属性，那就只修改属性就行
                            editorObj.editor.changeTextProps(linkProps);
                        } else {
                            // 更改了文字，直接在当前选区插入文字和新属性
                            editorObj.editor.insertText(value.text, linkProps);
                        }
                        // 更新编辑器光标位置和输入框位置
                        editorObj.editor.updateSurface();
                        editorObj.updateDataCB?.();
                    } else {
                        // 更改了文字，直接在当前选区插入文字和新属性
                        this.asyncLinkEdit(data, value, editorObj);
                    }
                    // 上报
                    let action;
                    if (hasHrefInFirstMarks) {
                        action = TDWActionType.LINK_EDIT;
                    } else {
                        action = TDWActionType.LINK_INSERT;
                    }
                    tdw({
                        opername: TDWOperationType.DOC_PPT,
                        module: TDWModuleType.PC_EDIT,
                        action,
                    });
                }
                this.linkEditMenuService.hide();
            },
        });
    };

    private processSavedStylesWithColorType = (newLinkData, colorType, editor) => {
        if (colorType !== 'tx') {
            return processSavedStyles({ href: newLinkData });
        }
        newLinkData.colorType = 'tx';
        const linkProps = processSavedStyles({ href: newLinkData });
        const textStyle = editor.getStyle();
        return { ...textStyle, ...linkProps };
    };

    private asyncLinkEdit = (linkData, hrefValue, editorObj) => {
        const { iconMode } = linkData;
        this.requestLinkData(hrefValue.url).then((data) => {
            if (linkData.text !== editorObj.editor.getString()) {
                // 如果文字或选区已经被修改，则不执行后续操作
                return;
            }
            let title = hrefValue.text;
            let mimeType = DEFAULT_MIMETYPE;
            if (data && data.retcode === 0) {
                ({ title } = data.result.linkInfo);
                ({ mimeType } = data.result.linkInfo);
            }
            const linkProps = processSavedStyles({ href: { ...hrefValue, mimeType, iconMode } });
            if (hrefValue.text === editorObj.editor?.getString()) {
                // 没有更改文字，只修改了链接属性，那就只修改属性就行
                editorObj.editor.changeTextProps(linkProps);
            } else {
                // 更改了文字，直接在当前选区插入文字和新属性
                editorObj.editor.insertText(hrefValue.text || title, linkProps);
            }
            // 更新编辑器光标位置和输入框位置
            editorObj.editor.updateSurface();
            editorObj.updateDataCB?.();
        });
    };

    /**
     * 删除文本链接
     * @param selection
     */
    private removeTextLink = (editor, updateDataCB) => {
        editor.removeTextProps('href');
        editor.removeTextProps('origin-href');
        updateDataCB?.();
    };

    /**
     * 获取链接主题色
     */
    private getLinkThemeColor = (slideNum?: number) => this.linkUtils.getLinkThemeColor(slideNum);

    /**
     * 获取当前选区或光标的链接信息
     * 如果是光标，获取光标所在节点的链接信息
     * 如果是选区，获取选区中第一个文字节点的链接信息（还原 office 规则）
     * @param {*} editor
     * @returns
     */
    private getCurrentLinkInfo(editor: Editor) {
        if (editor.isSelectionCollapsed()) {
            // 光标状态
            const styles = editor.getStyle();
            return styles?.['origin-href'];
        }
        // 选区状态的话，获取第一个文字节点的链接信息（这个逻辑是按照本地 office 来的）
        const [[startTextElement]] = editor.getSelectionNodes(node => node.type === 'text');
        return startTextElement?.['origin-href'];
    }

    private verifyTextToData = (verifyText) => {
        let data;
        if (isStrictlyCorrectUrl(verifyText)) {
            data = { url: verifyText };
        } else if (verifyText.indexOf(EMAIL_PREFIX) === 0) {
            data = { mail: transMailStr2Obj(verifyText) };
        } else if (isCorrectEmail(verifyText)) {
            data = { mail: transMailStr2Obj(`${EMAIL_PREFIX}${verifyText}`) };
        }
        return data;
    };

    /**
     * 对回车后的链接进行重命名
     * @param {} editor 文本框实例
     * @param {*} verifyText 超链接文本
     * @param {*} newSelection 选中范围
     */
    private renameLinkData = (editor, verifyText, linkRange, linkData, updateDataCB) => {
        const saveText = verifyText;
        this.requestLinkData(saveText).then((data) => {
            if (data && data.retcode === 0) {
                editor.setSelection(linkRange);
                if (editor.getString() !== verifyText) {
                    // 文本被修改，放弃重命名
                    updateDataCB?.();
                    return;
                }
                const { title, mimeType } = data.result.linkInfo;
                const style = processSavedStyles({ href: { ...linkData, mimeType, iconMode: true } });
                editor.insertText(title, style);
                updateDataCB?.();
            }
        });
    };

    private isSelectionHasLink = editor => !!editor.getStyle()['origin-href'];

    private isParagraphHasLink = (paragraph) => {
        if (!paragraph?.children) {
            return false;
        }
        return paragraph.children.some(text => !!text['origin-href']);
    };

    private async showLinkFloatBarPC(e, editorObj, option) {
        if (!editorObj?.editor || this.linkEditMenuService.isShown()) return;
        if (this.menuManager?.isMenuShown) return;
        const { addonManager } = this;
        let addon;
        if ((this.selectionService.selectedElements?.selectedArray?.length ?? 0) > 0) {
            addon = this.selectionService.selectedElements?.selectedArray?.[0]?.props?.addon;
        }
        const linkText = e.currentTarget;
        const { textElement } = linkText;
        const data = textElement?.['origin-href'];
        if (linkText) {
            this.linkFloatBarService.clearLeaveTimer();
            const href = this.getLinkUrlWithData(data);
            const hasChinese = data.slideJump;
            let isIframeLink = false;
            const isInNoteArea = this.isInNoteArea();
            if (!isInNoteArea) {
                isIframeLink = await addonManager.checkLink(href);
            }
            // 获取到和当前链接一致且相邻的 textElement 列表
            const sameLinkTextElements = SameLinkTextElementsHelper
                .getSameLinkTextElements(textElement, editorObj?.editor);
            const anchorRect = this.processFloatBarAnchorRect(sameLinkTextElements, isInNoteArea);
            this.linkFloatBarService.show({
                left: anchorRect.left,
                top: anchorRect.top,
                height: anchorRect.height,
                width: anchorRect.width,
                href,
                hasChinese,
                iconMode: !!data.iconMode,
                importConfig: this.linkFloatBarService.getDisplayPrivilege({
                    addon,
                    editState: this.userPriviledgeService.pptEditorState,
                    linkData: data,
                    isIframeLink,
                }), // 不是iframeLink
                addonManager: this.addonManager,
                menuManager: this.menuManager,
                selectionService: this.selectionService,
                openLink: () => {
                    this.openLink(data);
                },
                handleLinkRemove: () => {
                    if (option.onLinkRemove) {
                        option.onLinkRemove();
                    } else {
                        let textElements = sameLinkTextElements;
                        if (!editorObj?.editor?.isFocused) {
                            // 移除链接之前确保进入编辑态
                            editorObj?.editor?.focus();
                            // 进入编辑态之后数据可能会有改动，所以重新获取下 sameLinkTextElements
                            textElements = SameLinkTextElementsHelper
                                .getSameLinkTextElements(e.currentTarget.textElement, editorObj?.editor);
                        }
                        editorObj?.editor?.selectTextElements(textElements);
                        this.removeTextLink(editorObj?.editor, editorObj.updateDataCB);
                    }
                    this.linkFloatBarService.hide();
                },
                handleModeChange: (mode) => {
                    editorObj?.editor?.selectTextElements(sameLinkTextElements);
                    const text = editorObj?.editor?.getString();
                    this.handleModeChange(editorObj, { ...option, text }, data, mode);
                },
                deleteElement: () => {
                    this.selectionService.currentSlide?.deleteElement();
                },
                handleMouseLeave: () => {
                    this.linkFloatBarService.hideWithTimeout(500);
                },
                handleCopyUrl: () => {
                    this.linkFloatBarService.handleCopyUrl(href);
                    this.linkFloatBarService.handleTdwReport(TDWActionType.ADDON_COPY, addon, isIframeLink);
                },
                handleModifyUrl: () => {
                    if (option.onLinkEdit) {
                        option.onLinkEdit();
                    } else {
                        let textElements = sameLinkTextElements;
                        if (!editorObj?.editor?.isFocused) {
                            editorObj?.editor?.focus();
                            // 进入编辑态之后数据可能会有改动，所以重新获取下 sameLinkTextElements
                            textElements = SameLinkTextElementsHelper
                                .getSameLinkTextElements(e.currentTarget.textElement, editorObj?.editor);
                        }
                        editorObj?.editor?.selectTextElements(textElements);
                        if (this.editMenuTimer) {
                            clearTimeout(this.editMenuTimer);
                        }
                        this.editMenuTimer = setTimeout(() => {
                            this.setTextLink(editorObj, { ...data, text: editorObj?.editor?.getString() });
                            this.editMenuTimer = null;
                        }, 150);
                    }
                    this.linkFloatBarService.hide();
                },
                tdwOnSelectedItemCallback: (mode) => {
                    if (!addon) {
                        addon = addonManager.getIframeInfo();
                    }
                    this.linkFloatBarService.tdwOnSelectedItemCallback(mode, addon);
                },
            });
        }
    }

    private handleModeChange(editorObj, option, linkData, mode) {
        if (option.onModeChange) {
            option.onModeChange();
        } else {
            this.changeLinkMode(editorObj, option, linkData, mode);
        }
        this.linkFloatBarService.hide();
    }

    private async changeLinkMode(editorObj, option, linkData, mode) {
        const model = this.getSelectedModel(option.modelKey);
        const { editor, updateDataCB } = editorObj;
        const { selection } = editor;
        if (!model || !selection) {
            return;
        }
        const href = this.getLinkUrlWithData(linkData);
        const isIframeLink = await this.addonManager.checkLink(href);
        if (isIframeLink && mode === AddonMode.IFRAME) {
            this.changeIframeLinkMode(editor, href, mode, model, selection);
        } else {
            this.changeNormalLinkMode(editor, href, mode, linkData, option.text, updateDataCB);
        }
    }

    private changeNormalLinkMode = async (editor, href, mode, linkData, text, updateDataCB) => {
        const { mimeType } = linkData;
        const styles = editor.getStyle();
        if (mimeType) {
            const style = processSavedStyles({ href: { ...linkData, mimeType, iconMode: mode === 'icon' } });
            editor.insertText(text, { ...styles, ...style });
            editor.updateSurface();
            updateDataCB?.();
        } else {
            // 离线场景切换链接展示形式，不存在 mimeType 则返回默认的 DEFAULT_MIMETYPE
            let mimeType = DEFAULT_MIMETYPE;
            let title = text || href;
            if (network.isOnline) {
                await new Promise((resolve) => {
                    this.requestLinkData(href).then((data) => {
                        if (data && data.retcode === 0) {
                            title = data.result.linkInfo.title;
                            mimeType = data.result.linkInfo.mimeType;
                        }
                        resolve(null);
                    });
                });
            }
            const style = processSavedStyles({ href: { ...linkData, mimeType, iconMode: mode === 'icon' } });
            editor.insertText(text || title, { ...styles, ...style });
            editor.updateSurface();
            updateDataCB?.();
        }
    };

    private changeIframeLinkMode = (editor, href, mode, model, selection) => {
        const linkData = {
            url: href,
            text: editor.getString(),
        };
        // 先触发一下保存当前文本框的保存
        model.editorInstance?.triggerBlur();
        setTimeout(() => {
            this.addonManager.changeIframeMode(
                model, mode, linkData,
                {}, // 这个参数没用，只是为了兼容 addonManager 中代码
                // selection 选中了当前链接，需要过滤掉 selection 中的所有文字
                paragraphList => paragraphList.filter((paragraph, paragraphIndex) => {
                    if (editor.isIncludedInRange([paragraphIndex], selection)) {
                        // 整个段落都在选区内，直接过滤掉
                        return false;
                    }
                    const { textRuns } = paragraph;
                    // 当前段落不在选区内且当前段落为空，保留原有空段落
                    if (!textRuns || textRuns.length === 0) {
                        return true;
                    }
                    // 过滤掉当前段落中选区内的 textRun
                    paragraph.textRuns = textRuns.filter((textRun, textRunIndex) => {
                        const path = [paragraphIndex, textRunIndex];
                        return !editor.isIncludedInRange(path, selection);
                    });
                    // 过滤掉当前段落中选区内的 textRun 之后的空段落，直接过滤
                    return paragraph.textRuns.length !== 0;
                }),
            );
        }, 100);
    };

    /**
     * 处理链接跳转
     * @param data
     */
    private openLink = (data) => {
        if (data.slideJump) {  // 内部链接跳转
            const page = this.getJumpPage(
                data.slideJump,
                this.selectionService.currentSlideIndex, this.selectionService.slides.length,
            );
            if (this.selectionService.currentSlideIndex !== page) {
                this.selectionService.selectedElements.clear();
                this.selectionService.changeCurrentSlideIndexByUserAction(page);
            }
        } else {
            const href = this.getLinkUrlWithData(data);
            this.linkFloatBarService.openUrl(href);
        }
    };

    private updateTitleShowStatus = (worldBounds, hrefInfo, isShow) => {
        let left = 0;
        let top = 0;
        if (worldBounds) {
            left = (worldBounds.maxX + worldBounds.minX) / 2;
            top = worldBounds.maxY;
        }
        const title = this.getToolTip(hrefInfo, true);
        eventBus.emit(EventKeys.UPDATE_TITLE_SHOW_STATE, {
            visible: isShow,
            containerOffsetX: left,
            containerOffsetY: top,
            title,
        });
    };

    private isInNoteArea = () => this.selectionService.lastMouseDownRegion === RegionType.NOTE;

    private getSelectedModel = (modelKey) => {
        let model = this.selectionService.getSelectedModel();
        if (isMultiplyModel(model)) {
            const modelArray = model.getSelectedModels();
            modelArray?.forEach((m) => {
                if (modelKey === m.key) {
                    model = m;
                }
            });
        }
        return model;
    };

    private getCurrentLinkData = (editor, linkData?) => {
        // 已传入linkData，则直接使用
        if (linkData) {
            return linkData;
        }
        // 光标或当前选区存在链接，则编辑链接
        const currentLinkInfo = this.getCurrentLinkInfo(editor);
        if (currentLinkInfo) {
            // 光标状态，选中当前位置的链接
            if (editor.isSelectionCollapsed()) {
                const [[currentTextElement]] = editor.getSelectionNodes(node => node.type === 'text');
                const sameLinkTextElements = SameLinkTextElementsHelper
                    .getSameLinkTextElements(currentTextElement, editor);
                editor.selectTextElements(sameLinkTextElements);
            }
            return {
                ...currentLinkInfo,
                text: editor.getString(),
            };
        }
        // 选中的有文字，则编辑此段文字
        if (editor.getString()) {
            return { text: editor.getString() };
        }
        return null;
    };
}
